import {
  DashboardState,
  EDatasourceType,
  ESdChangeReason,
  EStatementLanguage,
  EStatementTypes,
  EValueType,
  IActionDefinitionDto,
  IActionParameter,
  IDatabaseColumnModel,
  IDatabaseTableModel,
  IDatasourceDto,
  IGenericSelectDataModel,
  RightPanelType
} from "@kuika/kuika-cl-model";
import Editor from "@monaco-editor/react";
import Form, { FormInstance } from "antd/es/form";
import _ from "lodash";
import type { IDisposable } from "monaco-editor";
import ObjectHash from "object-hash";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { DesignerSyncService } from "../../../services/designer-live/designer-sync-service";
import { LanguageServerService } from "../../../services/language-server-service";
import SqlService from "../../../services/sql-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import {
  attachSqlEditor,
  SqlLspContext,
  SqlSchemaTable,
  toLspLanguageId,
  updateSqlDiagnostics
} from "../../../shared/utility/sql-language-features";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { AICodeGenerator, EAICodeGeneratorType, IAICodeGeneratorSuggestion } from "../../common/ai/ai-code-generator";
import { IPlannedCodeInsertion } from "../../common/ai/ai-code-insertion";
import { AiPendingEdit } from "../../common/ai/ai-pending-edit";
import { AiPendingEditBar } from "../../common/ai/ai-pending-edit-bar";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { SQLActionParameters } from "../../common/ds/sql-action-parameters";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaEditorHeader from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { CodeEditorDrawer, ECodeEditorDrawerTab } from "../code-editor-shell/code-editor-drawer";
import { CodeEditorHeader } from "../code-editor-shell/code-editor-header";
import { CodeEditorProblemList } from "../code-editor-shell/code-editor-problem-list";
import { ICodeEditorProblem, readModelProblems, readRunErrorMessage } from "../code-editor-shell/code-editor-problems";
import {
  codeEditorRenameTestIds,
  ICodeEditorRenameTestIds,
  validateRenameField
} from "../code-editor-shell/code-editor-rename";
import { CodeEditorRenameTitle } from "../code-editor-shell/code-editor-rename-title";
import { CodeEditorResultTable } from "../code-editor-shell/code-editor-result-table";
import { CodeEditorSidePanel, ECodeEditorSideTab } from "../code-editor-shell/code-editor-side-panel";
import { CodeEditorSplitter } from "../code-editor-shell/code-editor-splitter";
import { CODE_EDITOR_THEME, defineCodeEditorTheme, watchCodeEditorTheme } from "../code-editor-shell/code-editor-theme";
import { CodeEditorToolbar, ICodeEditorCursor } from "../code-editor-shell/code-editor-toolbar";
import { GenericSqlPanelComponent } from "../left-panel/datasource-panel/generic-sql-panel";
import { requestDatasourceReveal } from "./datasource-reveal";
import { ActionSwitcherChip } from "./editor-switcher/action-switcher-chip";
import "./editor-bar-surface.scss";

/**
 * The select editor's rename row keeps the ids its suites were written against — the panel's
 * old "Approve" among them — and adds the pencil.
 */
const SELECT_ACTION_RENAME_TEST_IDS: ICodeEditorRenameTestIds = {
  input: "selectActionNameInput",
  edit: "selectActionNameEditButton",
  confirm: "selectActionNameApproveButton",
  cancel: "selectActionNameCancelButton"
};

interface ISqlEditorWrapperProps {
  "data-testid": string;
}

/**
 * @monaco-editor/react drops unknown props, so the SQL editor's test-id rides the wrapper it
 * renders around Monaco. Hoisted so every render hands the editor the same object.
 */
const SQL_EDITOR_WRAPPER_PROPS: ISqlEditorWrapperProps = { "data-testid": "sqlActionInput" };

/** The three example prompts KOA-13392 offers in the empty Kuika AI panel. */
const SQL_ACTION_AI_SUGGESTIONS: IAICodeGeneratorSuggestion[] = [
  { prompt: "Write a SQL query to fetch all records from this table", icon: "database-cog" },
  { prompt: "Filter results created in the last 30 days", icon: "filter-cog" },
  { prompt: "Select only active records", icon: "report" }
];

export interface ICreateActionState {
  code?: string;
  actionParameters: IActionParameter[];
  highlightedText: string | undefined;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  actionToEdit?: IActionDefinitionDto;
  newQuery: string;
  resultList: any[];
  fieldTemplates: any;
  resultListColumns: any[];
  /**
   * The committed name. The field mounts only while renaming, and the form hands back mounted
   * fields alone — so this, never the form, is what the hash, the save and the dialogs read.
   */
  actionName: string;
  isNameOnEdit: boolean;
  shouldGenerateWithAI: boolean;
  isResultListOpen: boolean;
  selectedPunto: number;
  submitButtonDisabledStatus: boolean;
  isSidePanelOpened: boolean;
  wrapStatus: string;
  isParametersLoading: boolean;
  /** Parameter names a type lookup has already come back for — see SQLActionParameters. */
  checkedTypeNames: string[];
  typingTimeout?: any;
  genericSelectData: IGenericSelectDataModel | undefined;
  isTestButtonLoading: boolean;
  isCreating: boolean;
  formatCompleted: boolean;
  /** The AI edit waiting for the user's confirmation; the editor still holds their own query. */
  pendingAiEdit: IPlannedCodeInsertion | null;
  drawerTab: ECodeEditorDrawerTab;
  sideTab: ECodeEditorSideTab;
  /** Null until the editor reports a cursor. */
  cursor: ICodeEditorCursor | null;
  /** Whatever the SQL language server currently complains about. */
  problems: ICodeEditorProblem[];
  /** What the database refused the last run with; cleared by the next one. */
  runError: string | null;
  /** How long the last run took, for the drawer's summary. */
  lastRunDurationMs: number | null;
  /** Dragged by the grips, so both panels keep the size the user gave them. */
  drawerHeight: number;
  /** Folded from the drawer's own grip; the editor takes the room. */
  isResultDrawerOpen: boolean;
  sidePanelWidth: number;
}

export interface ICreateActionProps {
  datasource?: IDatasourceDto;
  actionToEdit?: IActionDefinitionDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  isNewGenericSqlAction?: boolean;
}

class CreateAction extends PureSdComponent<ICreateActionProps & IWithSDProps, ICreateActionState> {
  formRef = React.createRef<FormInstance>();

  testResultsRef = React.createRef<HTMLDivElement>();

  editorRef = React.createRef();

  lastClickedRowRef = React.createRef<number>();

  sqlLspDisposable: IDisposable | undefined;

  markerDisposable: IDisposable | undefined;

  sqlMonaco: typeof import("monaco-editor") | undefined;
  codeThemeObserver: MutationObserver | undefined;

  sqlEditor: import("monaco-editor").editor.IStandaloneCodeEditor | undefined;

  aiPendingEdit: AiPendingEdit = new AiPendingEdit({
    onChange: (pendingAiEdit: IPlannedCodeInsertion | null) => this.setState({ pendingAiEdit })
  });

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateActionProps & IWithSDProps) {
    super(props);
    let genericSelectData: undefined | IGenericSelectDataModel;
    if (props.actionToEdit?.genericSelectData) {
      genericSelectData = props.actionToEdit.genericSelectData;
    } else if (props.isNewGenericSqlAction) {
      genericSelectData = {
        tables: [],
        fields: [],
        relations: [],
        whereData: [],
        sortData: []
      } as IGenericSelectDataModel;
    }
    this.state = {
      code: "",
      actionParameters: [],
      highlightedText: "",
      saveStatus: "idle",
      actionToEdit: this.props.actionToEdit,
      newQuery: "",
      resultList: [],
      resultListColumns: [],
      fieldTemplates: {},
      actionName: props.actionToEdit?.name ?? props.sd.activeCreatingActionName ?? "",
      // A new action opens on its generated name with the field open, so naming it is the
      // first thing offered; an existing one opens on its name.
      isNameOnEdit: props.actionToEdit === undefined,
      isResultListOpen: false,
      shouldGenerateWithAI: false,
      selectedPunto: 12,
      submitButtonDisabledStatus: true,
      isSidePanelOpened: true,
      wrapStatus: "on",
      isParametersLoading: false,
      checkedTypeNames: [],
      typingTimeout: undefined,
      genericSelectData,
      isTestButtonLoading: false,
      isCreating: false,
      formatCompleted: false,
      pendingAiEdit: null,
      drawerTab: "result",
      sideTab: "coach",
      cursor: null,
      problems: [],
      runError: null,
      lastRunDurationMs: null,
      drawerHeight: 258,
      isResultDrawerOpen: false,
      sidePanelWidth: 288
    };
  }

  componentDidUpdate(prevProps: ICreateActionProps & IWithSDProps) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    if (prevProps.actionToEdit != this.props.actionToEdit && !this.props.actionToEdit) {
      const actionName: string = this.props.sd.activeCreatingActionName ?? "";
      fieldsValue.Statement = "";
      fieldsValue.ActionName = actionName;
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.setState(
        {
          code: "",
          actionParameters: [],
          actionToEdit: this.props.actionToEdit,
          actionName,
          isNameOnEdit: true,
          resultList: [],
          resultListColumns: [],
          isResultListOpen: false,
          genericSelectData: this.props.sd.isGenericSelectActionCreating
            ? ({
                tables: [],
                fields: [],
                relations: [],
                whereData: [],
                sortData: []
              } as IGenericSelectDataModel)
            : undefined
        },
        () => {
          this.objectHash = this.getObjectHash();
        }
      );
    } else if (
      this.props.actionToEdit &&
      this.props.actionToEdit !== undefined &&
      prevProps.actionToEdit != this.props.actionToEdit
    ) {
      this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
      fieldsValue.Statement = this.props.actionToEdit.statement;
      fieldsValue.ActionName = this.props.actionToEdit.name;
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.setState(
        {
          code: this.props.actionToEdit.statement,
          actionParameters: this.props.actionToEdit.actionParameters,
          actionToEdit: this.props.actionToEdit,
          actionName: this.props.actionToEdit.name,
          isNameOnEdit: false,
          resultList: [],
          resultListColumns: [],
          isResultListOpen: false,
          submitButtonDisabledStatus: true,
          genericSelectData: this.props.actionToEdit?.genericSelectData
        },
        () => {
          this.objectHash = this.getObjectHash();
        }
      );
    } else if (this.props.sd.actionScript && this.props.sd.actionScript !== prevProps.sd.actionScript) {
      if (!fieldsValue.Statement) {
        fieldsValue.Statement = this.props.sd.actionScript;
      } else {
        fieldsValue.Statement += ` ${this.props.sd.actionScript}`;
      }
      this.props.sd.setActionScript(undefined, "empty", undefined);
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.onChange(fieldsValue.Statement);
    } else if (this.props.sd.queryBuilderSql && this.props.sd.queryBuilderSql !== prevProps.sd.queryBuilderSql) {
      // The statement only: the name is the editor's own state, and writing the edited
      // action's name back here blanked a NEW action's, which then saved nameless.
      fieldsValue.Statement = this.state.newQuery;
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.setState({ code: fieldsValue.Statement }, () => {
        this.objectHash = this.getObjectHash();
      });
    }

    if (!prevProps.sd.openActionWithQueryBuilder && this.props.sd.openActionWithQueryBuilder) {
      this.openActiveQueryBuilder();
    }
    if (prevProps.sd.isGenericSelectActionCreating !== this.props.sd.isGenericSelectActionCreating) {
      this.setState(
        {
          genericSelectData: this.props.sd.isGenericSelectActionCreating
            ? ({
                tables: [],
                fields: [],
                relations: [],
                whereData: [],
                sortData: []
              } as IGenericSelectDataModel)
            : undefined
        },
        () => {
          this.objectHash = this.getObjectHash();
        }
      );
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  checkAndHandleGenerateParameterTypes = () => {
    const currentParams = JSON.stringify(this.state.actionParameters);
    if (currentParams.includes("Unknown")) {
      this.handleGenerateParameters();
    }
  };

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.sqlLspDisposable?.dispose();
    this.markerDisposable?.dispose();
    this.codeThemeObserver?.disconnect();
    this.aiPendingEdit.dispose();
  }

  componentDidMount() {
    let actionParameters = this.props?.actionToEdit?.actionParameters;
    let state = _.cloneDeep(this.state);
    if (!actionParameters) actionParameters = [];

    if (this.props.sd.openActionWithQueryBuilder) {
      this.openActiveQueryBuilder();
    }

    state = {
      ...state,
      actionParameters,
      // Loaded from the saved action, so their types are whatever the last lookup settled on: one
      // still Unknown here is genuinely unresolved and says so straight away.
      checkedTypeNames: actionParameters.map((parameter: IActionParameter): string => parameter.name),
      code: this.props.actionToEdit?.statement ?? ""
    };

    if (this.props.sd.isGenericSelectActionCreating)
      state = {
        ...state,
        genericSelectData: {
          tables: [],
          fields: [],
          relations: [],
          whereData: [],
          sortData: []
        } as IGenericSelectDataModel
      };

    this.formRef.current?.setFieldsValue({
      Statement: this.props.actionToEdit?.statement || "",
      ActionName: this.state.actionName
    });
    this.setState(state, () => {
      this.objectHash = this.getObjectHash();
    });

    setTimeout(() => {
      this.handleGenerateParameters();
    }, 2000);
  }

  setSubmitButtonDisabledStatus() {
    if (!this.formRef.current) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    if (!this.formRef.current.getFieldValue("Statement")) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    if (this.objectHash === this.getObjectHash() && !this.state.genericSelectData?.statementData?.isConvertedToSql) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }
    this.setState({ submitButtonDisabledStatus: false });
  }

  handleEditorMount = (editor) => {
    this.editorRef = editor;
    this.sqlEditor = editor;
    if (this.sqlMonaco) {
      this.sqlLspDisposable = attachSqlEditor(this.sqlMonaco, editor.getModel(), this.getSqlLspContext());
      this.aiPendingEdit.attach(this.sqlMonaco, editor);
    }
    editor.onDidChangeCursorSelection(this.getHighLightedText);
    editor.onDidChangeCursorPosition((event) => this.setState({ cursor: event.position }));
    editor.updateOptions({ fontSize: this.state.selectedPunto });
    this.markerDisposable = this.sqlMonaco?.editor.onDidChangeMarkers(() => this.refreshProblems());
    editor.onMouseUp((event) => {
      const position = event.target.position;
      this.lastClickedRowRef = position.lineNumber;
    });
    editor.updateOptions({ wordWrap: "on" });
  };

  handleWrap = (wrapType: string) => {
    const editor: any = this.editorRef;
    if (editor) {
      editor.updateOptions({ wordWrap: wrapType });
      this.setState({ wrapStatus: wrapType });
    }
  };

  handlePasteToLastClickedRow = () => {
    const editor = this.editorRef;
    const lastClickedRow = this.lastClickedRowRef;

    if (editor && lastClickedRow) {
      navigator.clipboard
        .readText()
        .then((text) => {
          // @ts-ignore
          editor?.trigger("keyboard", "type", { text });
        })
        .catch((error) => {
          KMainFunctions.exceptionHandler(error);
        });
    }
  };

  getObjectHash(): string {
    const { Statement } = this.formRef.current?.getFieldsValue() || {};
    const { actionName: ActionName, actionParameters, genericSelectData } = { ...this.state };

    return ObjectHash({ ActionName, Statement, actionParameters, genericSelectData });
  }

  handleStartRename = (): void => {
    this.formRef.current?.setFieldsValue({ ActionName: this.state.actionName });
    this.setState({ isNameOnEdit: true });
  };

  /** Commits the typed name once its rules pass. */
  handleConfirmRename = async (): Promise<string | undefined> => {
    const actionName: string | undefined = await validateRenameField(this.formRef.current, "ActionName");
    if (actionName === undefined) return undefined;
    this.setState({ actionName, isNameOnEdit: false });
    return actionName;
  };

  handleAbortRename = (): void => {
    this.formRef.current?.setFieldsValue({ ActionName: this.state.actionName });
    this.setState({ isNameOnEdit: false });
  };

  /** The name a save writes: the committed one, or — mid-rename — the typed one once it passes. */
  resolveNameForSave = async (): Promise<string | undefined> => {
    if (this.state.isNameOnEdit) return this.handleConfirmRename();
    if (!this.state.actionName) {
      KMainFunctions.displayWarningNotification("Please enter an Action name");
      return undefined;
    }
    return this.state.actionName;
  };

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
    this.props.sd.loadActionDefinitions();
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  editorBeforeMount = (monacoInstance: typeof import("monaco-editor")) => {
    this.sqlMonaco = monacoInstance;
    defineCodeEditorTheme(monacoInstance);
    this.codeThemeObserver = watchCodeEditorTheme(monacoInstance);
  };

  getSqlLspContext = (): SqlLspContext => ({
    getDatasourceId: () => this.props.datasource?.id ?? null,
    getLanguageId: () => toLspLanguageId(this.props.datasource?.datasourceType),
    getDatasourceType: () => this.props.datasource?.datasourceType,
    getSchema: () => this.getClientSchemaTables(),
    ensureColumns: (tableNames: string[]) => this.ensureSchemaColumnsLoaded(tableNames)
  });

  /** Normalizes the active datasource's loaded schema into the shape the SQL completion provider expects. */
  getClientSchemaTables = (): SqlSchemaTable[] => {
    const datasourceId: string | undefined = this.props.datasource?.id;
    if (!datasourceId) return [];
    const tables: IDatabaseTableModel[] = this.props.sd?.schemas?.get(datasourceId)?.tables ?? [];
    return tables
      .filter((table: IDatabaseTableModel): boolean => !table.dropped)
      .map((table: IDatabaseTableModel): SqlSchemaTable => ({
        name: table.title || table.name,
        schemaName: table.schema,
        columns: (table.columns ?? [])
          .filter((column: IDatabaseColumnModel): boolean => !column.dropped)
          .map((column: IDatabaseColumnModel): string => column.name)
      }));
  };

  /** Hydrates columns for referenced tables (KOA-17026 slim load) so column autocomplete works. */
  ensureSchemaColumnsLoaded = async (tableNames: string[]): Promise<void> => {
    const datasourceId: string | undefined = this.props.datasource?.id;
    if (!datasourceId || !this.props.sd?.ensureTableColumnsLoaded) return;
    await this.props.sd.ensureTableColumnsLoaded(datasourceId, tableNames);
  };

  handleFormatSql = async () => {
    const editor = this.sqlEditor;
    const model = editor?.getModel();
    if (!editor || !model) return;

    const sourceCode = model.getValue();
    const languageId = toLspLanguageId(this.props.datasource?.datasourceType);
    const formattedCode = await LanguageServerService.Format(sourceCode, languageId);
    if (!formattedCode) return;

    editor.executeEdits("format", [{ range: model.getFullModelRange(), text: formattedCode }]);
    this.formRef.current?.setFieldsValue({ Statement: formattedCode });
    this.setState({ code: formattedCode, formatCompleted: true });
    updateSqlDiagnostics(this.sqlMonaco, model, formattedCode, this.getSqlLspContext());
    setTimeout(() => this.setState({ formatCompleted: false }), 3000);
  };

  /** Writes the AI edit the user just confirmed and re-runs what typing would have triggered. */
  handleApplyPendingAiEdit = () => {
    const newValue = this.aiPendingEdit.commit();
    if (newValue === null) return;

    this.formRef.current?.setFieldsValue({ Statement: newValue });
    this.setState({ code: newValue });
    updateSqlDiagnostics(this.sqlMonaco, this.sqlEditor?.getModel(), newValue, this.getSqlLspContext());
  };

  getHighLightedText = () => {
    const editor: any = this.editorRef;
    const selection = editor?.getSelection?.();
    const highlightedText =
      selection && !selection.isEmpty() ? (editor?.getModel?.()?.getValueInRange(selection) as string) : "";
    this.setState({ highlightedText });
  };

  onChange = (newValue) => {
    clearTimeout(this.state.typingTimeout);

    updateSqlDiagnostics(this.sqlMonaco, this.sqlEditor?.getModel(), newValue, this.getSqlLspContext());

    const newTimeout = setTimeout(() => {
      this.checkAndHandleGenerateParameterTypes();
    }, 2000);

    const paramsFromCode = SqlService.getSqlParameters(this.props.datasource?.datasourceType, newValue);

    const newParameters = [] as IActionParameter[];
    const currentActionParameters = [...this.state.actionParameters];

    paramsFromCode.forEach((paramFromCode) => {
      paramFromCode = paramFromCode.replace(/^@/, "").replace(/^:/, "");
      const curData = currentActionParameters.find((x) => x.name === paramFromCode);
      const paramObj = {} as IActionParameter;
      paramObj.name = paramFromCode;
      paramObj.Typename = curData ? curData.Typename : "Unknown";
      newParameters.push(paramObj);
    });

    if (!newValue) {
      this.setState({ highlightedText: "" });
    }
    this.props.sd.refreshActionParameters(newParameters);
    this.props.sd.refreshActionStatement(newValue);
    this.setState({ actionParameters: newParameters, code: newValue, typingTimeout: newTimeout });
  };

  nameValidator = async (_rule, value) => {
    if (!value) throw new Error("Please enter an Action name");
    if (!/^[A-Za-z][A-Za-z0-9_]+$/.test(value)) throw new Error("Action name is invalid");
  };

  isUnknownParameterExists = (parameters: IActionParameter[]) => {
    if (parameters == undefined) {
      return false;
    }
    const isUnknownExist = parameters.find((x) => x.Typename == "Unknown");

    if (isUnknownExist) {
      return false;
    }

    return true;
  };

  isPostgreQueryUseParams = (action: IActionDefinitionDto) => {
    return (
      (this.props?.datasource?.datasourceType === EDatasourceType.Postgre ||
        this.props?.datasource?.datasourceType === EDatasourceType.ManagedDbPostgre) &&
      (action.actionParameters?.length || action.parameters?.length) &&
      /\bbegin\b.*\bend\b|\bif\b/i.test(action.statement)
    );
  };

  saveAction = async (data: any) => {
    if (this.props.datasource?.id === undefined) {
      return;
    }
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    this.setState({ isCreating: true });

    // let isActionExist = this.props.sd?.actionDefinitions?.find((x) => x.name.toLowerCase() === this.state.actionName?.toLowerCase());
    // if (isActionExist && !this.state.actionToEdit) {
    //   KMainFunctions.displayWarningNotification("Action name already exist.");
    //   this.setState({ isSaveButtonDisabled: false });
    //   return;
    // }

    // One name for both editors — the select one and the SQL one it converts into.
    const actionName: string | undefined = await this.resolveNameForSave();
    if (!actionName) {
      this.saveStatus.reset();
      this.setState({ isCreating: false });
      return;
    }
    const action = {} as IActionDefinitionDto;
    action.kDatasource_Id = this.props.datasource.id;
    action.name = actionName;

    if (this.state.genericSelectData && this.state.genericSelectData?.statementData?.isConvertedToSql !== true) {
      if (!this.state.genericSelectData.tables || this.state.genericSelectData.tables?.length === 0) {
        KMainFunctions.displayWarningNotification("Please select at least one source.");
        this.saveStatus.reset();
        this.setState({ isCreating: false });
        return;
      }
      if (this.state.genericSelectData.hasValidationError === true) {
        KMainFunctions.displayWarningNotification("Please fix the validation errors to save the action.");
        this.saveStatus.reset();
        this.setState({ isCreating: false });
        return;
      }
      const statementReq = await DatasourceService.GetGenericSelectDataSql(
        this.props.datasource.id,
        this.state.genericSelectData as IGenericSelectDataModel
      );
      if (statementReq.status !== 200) {
        this.saveStatus.reset();
        this.setState({ isCreating: false });
        return;
      }
      action.statement = statementReq.data;
      action.statementType = EStatementTypes.Select;
      action.genericSelectData = this.state.genericSelectData;
      action.parameters = KMainFunctions.calculateGenericSelectParameters(this.state.genericSelectData);
    } else {
      action.statement = data.Statement;
      action.parameters = this.state.actionParameters;
      if (!this.isUnknownParameterExists(action.parameters)) {
        await this.handleGenerateParameters();
      }
    }

    // PL/pgSQL codes with parameters is not supported in npgsql library
    if (this.isPostgreQueryUseParams(action)) {
      KMainFunctions.displayWarningNotification(
        "PL/pgSQL script is not supported with parameters! Please use functions instead"
      );
      this.saveStatus.reset();
      this.setState({ isCreating: false });
      return;
    }

    if (!this.isUnknownParameterExists(action.parameters)) {
      KMainFunctions.displayWarningNotification("Please fill parameter types before save!!");
      this.saveStatus.reset();
      this.setState({ isCreating: false });
      return;
    }

    if (this.state.actionToEdit && this.state.actionToEdit.id) {
      action.id = this.state.actionToEdit.id;
      // Stale-write gate only for UPDATES of an existing action — creates cannot conflict.
      const proceed: boolean = await DesignerSyncService.checkStaleWrite(
        "action.changed",
        action.id,
        action.name ?? "Action"
      );
      if (!proceed) {
        this.saveStatus.reset();
        this.setState({ isCreating: false });
        return;
      }
    }

    try {
      const res = await DatasourceService.SaveAction(action);
      if (res.status != 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }

      await this.props.sd.loadActionDefinitions();
      const resData = res.data;
      const addedAction = resData?.find((x) => x.name === action.name);
      const actionId = addedAction?.id;
      if (this.props.sd?.creatingDatasouceFolderId && actionId) {
        const folder = this.props.sd.getFolderById(
          this.props.sd?.creatingDatasouceFolderId,
          this.props.sd.project.actionFolders
        );
        if (folder && folder.parentFolderId) {
          this.props.sd.addActionToFolderInFolder(
            folder.parentFolderId,
            this.props.sd?.creatingDatasouceFolderId,
            actionId
          );
        } else {
          this.props.sd.addActionToFolder(this.props.sd?.creatingDatasouceFolderId, actionId);
        }
      }
      // this.props.sd.setLastAddedAction(actionId, this.props.datasource?.id);
      // this.props.sd.setEditingActionsDatasource(this.props.datasource, addedAction, false, false);
      // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
      // A close confirmation this save answered ("Update & close") steps aside at once, so
      // the ✓ on the button is what is seen before the editor leaves.
      this.props.setShowModal(false);
      this.saveStatus.succeed((): void => {
        // The list behind the editor lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: action.name ?? "", parentId: this.props.datasource?.id });
        this.closeDataSource();
      });
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
      return error;
    } finally {
      this.setState({ isCreating: false });
    }
  };

  handleTest = async () => {
    if (this.props.datasource?.id === undefined) {
      return;
    }
    const action = {} as IActionDefinitionDto;
    action.kDatasource_Id = this.props.datasource.id;
    action.statement =
      this.state.highlightedText && this.state.highlightedText.trim().length > 0
        ? this.state.highlightedText
        : this.formRef.current?.getFieldValue("Statement");
    if (action.statement === undefined) {
      action.statement = "";
    }
    action.actionParameters = this.state.actionParameters;

    if (this.state.actionToEdit && this.state.actionToEdit.id) {
      action.id = this.state.actionToEdit.id;
    }

    // PL/pgSQL codes with parameters are not supported in npgsql library
    if (this.isPostgreQueryUseParams(action)) {
      KMainFunctions.displayWarningNotification(
        "PL/pgSQL script is not supported with parameters! Please use functions instead"
      );
      this.saveStatus.reset();
      return;
    }

    if (!this.isUnknownParameterExists(action.actionParameters)) {
      KMainFunctions.displayWarningNotification("Please fill parameter types before save!!");
      this.saveStatus.reset();
      return;
    }

    try {
      this.setState({ isTestButtonLoading: true, runError: null });
      const startedAt: number = Date.now();
      const res = await DatasourceService.ExecuteQuery(action, true);
      if (res && res.status === 200) {
        this.setState({
          resultList: res.data.resultList,
          fieldTemplates: res.data.fieldTemplates,
          isResultListOpen: true,
          lastRunDurationMs: Date.now() - startedAt,
          drawerTab: "result",
          // A run always shows its result, even if the drawer was folded away.
          isResultDrawerOpen: true
        });
        this.createColumnsForActionResult(res.data.resultList);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      // A run that failed is exactly when the console is wanted, and what it has to say is
      // on the Messages tab. The toast says it once and goes; the tab keeps it.
      this.setState({
        runError: readRunErrorMessage(error),
        drawerTab: "messages",
        isResultDrawerOpen: true
      });
      return error;
    } finally {
      this.setState({ isTestButtonLoading: false });
    }
  };

  createColumnsForActionResult = (resultList: any) => {
    const columns = [] as any[];
    if (resultList && resultList.length > 0) {
      resultList.forEach((result) => {
        for (const [key, value] of Object.entries(result)) {
          if (
            columns.findIndex((item) => item.dataIndex === key) === -1 &&
            !key.toLocaleLowerCase().includes("_deleted")
          ) {
            columns.push({
              title: key,
              dataIndex: key,
              key,
              width: "100px",
              value
            });
          }
        }
      });
      if (columns.length > 0) {
        this.setState({ resultListColumns: columns });
        return;
      }
    }

    this.setState({
      resultListColumns: Object.keys(this.state.fieldTemplates).map((fieldKey) => ({
        title: fieldKey,
        dataIndex: fieldKey,
        key: fieldKey,
        width: "100px",
        value: null
      }))
    });
  };

  openActiveQueryBuilder = () => {
    if (this.props.datasource?.id === undefined) {
      return;
    }
    const fieldsValue = this.formRef.current?.getFieldsValue();
    const action = {} as IActionDefinitionDto;
    action.kDatasource_Id = this.props.datasource.id;
    action.actionParameters = this.state.actionParameters;
    if (this.state.actionName) {
      action.name = this.state.actionName;
    }
    action.statement = fieldsValue.Statement;

    let editingQuery: string;
    if (!this.state.highlightedText) {
      editingQuery = "";
      this.props.sd.setIsNewQuery(true);
    } else {
      editingQuery = this.state.highlightedText;
      this.props.sd.setIsNewQuery(false, this.state.highlightedText);
    }

    if (this.state.actionToEdit && this.state.actionToEdit.id) {
      action.id = this.state.actionToEdit.id;
    }

    this.props.sd.queryBuilderActionEdit(action, editingQuery, this.callbackQueryBuilder);
  };

  callbackQueryBuilder = (sql: string) => {
    const { actionToEdit } = this.state;
    let replacedQuery: string = "";
    const fieldsValue = this.formRef.current?.getFieldsValue();

    if (actionToEdit) {
      if (!this.props.sd.highlightedQuery || this.props.sd.highlightedQuery === "") actionToEdit.statement = sql;
      else {
        replacedQuery = this.editQuery(sql, actionToEdit.statement);
        actionToEdit.statement = replacedQuery;
      }
    } else if (!this.props.sd.highlightedQuery || this.props.sd.highlightedQuery === "") fieldsValue.Statement = sql;
    else {
      replacedQuery = this.editQuery(sql, fieldsValue.Statement);
      fieldsValue.Statement = replacedQuery;
    }
    this.setState({
      actionToEdit: actionToEdit && actionToEdit,
      newQuery: this.props.sd.highlightedQuery ? replacedQuery : sql
    });
    // The query builder is an overlay (`queryBuilderIsOpen`), not a tool, so this was never a return-from
    // navigation — it is an assumption that the editor being filled in lives on the data diagram. That stopped
    // being true when Document Parameters started hosting this same editor for a parameter's source action
    // (KOA-17254): applying a generated query ejected the user from the tool they were working in.
    if (this.props.sd.isEditingDocumentParameterSourceAction !== true) {
      this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
    }
  };

  editQuery = (sql: string, statement: any) => {
    let replacedQuery: string = "";
    replacedQuery = statement.replace(this.props.sd.highlightedQuery, sql);
    return replacedQuery;
  };

  getQueryText(): React.ReactNode {
    return this.state.highlightedText && this.state.highlightedText.length > 0 ? (
      <span
        style={
          !this.state.highlightedText?.toLowerCase().startsWith("select")
            ? { color: "#bfbfbf", fontSize: "12px" }
            : { color: "#246EFF", fontSize: "12px" }
        }
      >
        Edit with Query Builder
      </span>
    ) : (
      "New Query with Query Builder"
    );
  }

  handleEditorPuntoChange = (fontSize: number) => {
    this.sqlEditor?.updateOptions({ fontSize });
    this.setState({ selectedPunto: fontSize });
  };

  /** Copies the selection when there is one, otherwise the whole editor. */
  handleCopyEditorContent = async (): Promise<void> => {
    const editor = this.sqlEditor;
    const model = editor?.getModel();
    if (!editor || !model) return;

    const selection = editor.getSelection();
    const hasSelection: boolean = !!selection && !selection.isEmpty();
    const text: string = hasSelection && selection ? model.getValueInRange(selection) : model.getValue();
    // The shared write: the Clipboard API, then the textarea fallback when permission is refused.
    await copyTextToClipboard(text);
  };

  handleUndo = () => {
    this.sqlEditor?.trigger("toolbar", "undo", null);
  };

  handleRedo = () => {
    this.sqlEditor?.trigger("toolbar", "redo", null);
  };

  refreshProblems = () => {
    this.setState({ problems: readModelProblems(this.sqlMonaco, this.sqlEditor?.getModel()) });
  };

  /** The Kuika AI dialect, or null for datasources it cannot help with. */
  getCoachType = (): EAICodeGeneratorType | null => {
    const type = this.props.datasource?.datasourceType;
    if (type === EDatasourceType.CustomDb || type === EDatasourceType.ManagedDb) return EAICodeGeneratorType.SQL;
    if (type === EDatasourceType.Postgre || type === EDatasourceType.ManagedDbPostgre) {
      return EAICodeGeneratorType.Postgre;
    }
    if (type === EDatasourceType.Oracle || type === EDatasourceType.ManagedDbOracle) return EAICodeGeneratorType.Oracle;
    return null;
  };

  getRunMeta = (): string | undefined => {
    if (!this.state.isResultListOpen) return undefined;
    const rows: number = this.state.resultList.length;
    const rowLabel: string = `${rows} ${rows === 1 ? "row" : "rows"}`;
    if (this.state.lastRunDurationMs === null) return rowLabel;
    return `${rowLabel} · ${this.state.lastRunDurationMs} ms`;
  };

  renderResultTable = (): JSX.Element => {
    if (!this.state.isResultListOpen) {
      return <div className="kuika__code-editor__drawer__empty">Run the query to see what it returns.</div>;
    }

    return (
      <CodeEditorResultTable
        rows={this.state.resultList}
        columns={this.state.resultListColumns}
        drawerHeight={this.state.drawerHeight}
        onboardingId="data-diagram_create-action_test-result-table"
      />
    );
  };

  goToProblem = (problem: ICodeEditorProblem): void => {
    const editor = this.sqlEditor;
    if (!editor) return;

    editor.revealLineInCenterIfOutsideViewport(problem.lineNumber);
    editor.setPosition({ lineNumber: problem.lineNumber, column: problem.column });
    editor.focus();
  };

  renderProblems = (): JSX.Element => (
    <CodeEditorProblemList problems={this.state.problems} runError={this.state.runError} onSelect={this.goToProblem} />
  );

  handleGenerateParameters = async () => {
    if (!this.props.datasource?.id) return;
    this.setState({ isParametersLoading: true });
    const statement = this.formRef.current?.getFieldValue("Statement");
    const parameters = this.state.actionParameters.map((x) => {
      return { name: x.name, Typename: x.Typename };
    });

    try {
      const res: any = await DatasourceService.GenerateSqlParamTypes(this.props.datasource?.id, statement, parameters);
      if (res && res.data && res.data.isSuccess) {
        let params: any = [...this.state.actionParameters];
        const newParameters = res.data.parameters;
        params.forEach((parameter: any) => {
          if (parameter.Typename === "Unknown") {
            const newParameter = newParameters.find((x) => x.name === parameter.name);
            if (newParameter) {
              parameter.Typename = EValueType[newParameter.valueType].toString();
            }
          }
        });
        const notExistedParams = newParameters.filter((x) => !params.find((y) => y.name === x.name));
        notExistedParams.forEach((x: any) => {
          params.push({ name: x.name, Typename: EValueType[x.valueType].toString() });
        });
        this.setState({ actionParameters: params });
      }
    } catch (error) {
      return error;
    } finally {
      // Asked, whatever came back. A name still Unknown after this is one the lookup could not
      // settle — it blocks saving, so from here on it is flagged. A failed or refused lookup counts:
      // the type stays unresolved either way, and what must not flag them is the wait before asking.
      const checkedTypeNames: string[] = Array.from(
        new Set([...this.state.checkedTypeNames, ...parameters.map((x): string => x.name)])
      );
      this.setState({ isParametersLoading: false, checkedTypeNames });
    }
  };

  convertGenericSelectToSql = async (sqlString: string) => {
    if (!this.state.genericSelectData) return;
    const parameters = await KMainFunctions.calculateGenericSelectParameters(this.state.genericSelectData);
    const genericSelectData = _.cloneDeep(this.state.genericSelectData);
    genericSelectData.statementData = {
      createdAt: new Date(),
      isConvertedToSql: true,
      statement: sqlString,
      statementLanguage: EStatementLanguage.MsSql
    };
    // The name carries over as it is: both editors read the one `actionName`, and a rename
    // still open on the select editor stays open on the SQL one.
    this.setState({
      genericSelectData,
      actionParameters: parameters,
      submitButtonDisabledStatus: false
    });
    this.formRef.current?.setFieldsValue({ Statement: sqlString });
    this.props.sd.setGenericSelectActionCreatingStatus(false);
  };

  isGenericSelectAction = () => {
    return (
      (!!this.state.genericSelectData &&
        this.state.genericSelectData?.tables?.length > 0 &&
        this.state.genericSelectData.statementData?.isConvertedToSql !== true) ||
      this.props.sd.isGenericSelectActionCreating
    );
  };

  render() {
    this.setSubmitButtonDisabledStatus();

    const datasourceSchema = this.props.sd.schemas.get(this.props.datasource?.id ?? "");
    let tables: string[] = [];
    if (datasourceSchema) {
      tables = datasourceSchema.tables.map((table) => table.title);
    }
    const isGenericSelectAction = this.isGenericSelectAction();
    const coachType: EAICodeGeneratorType | null = this.getCoachType();

    return (
      <Form
        ref={this.formRef}
        onFinish={this.saveAction}
        // A new action's name field mounts open, so it has to hold the generated name on its
        // first paint: filled a tick later, its focus selects nothing and typing appends.
        initialValues={
          this.state?.actionToEdit && this.state?.actionToEdit.id
            ? { Statement: this.state.actionToEdit.statement, ActionName: this.state.actionToEdit.name }
            : { ActionName: this.state.actionName }
        }
        onValuesChange={() => {
          this.setSubmitButtonDisabledStatus();
        }}
        layout="vertical"
        // Both editors fill the panel they were given. The select action used to measure itself
        // against the viewport instead, carrying the chrome beside it as a hand-summed constant
        // — and that constant left it 50px short of the card it sits in.
        style={{ height: "100%", width: "100%" }}
      >
        <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar">
          {isGenericSelectAction ? (
            <>
              <KuikaEditorHeader
                title={this.state.actionName || "Select Action"}
                renderTitle={(): React.ReactNode => (
                  <CodeEditorRenameTitle
                    identity={
                      <ActionSwitcherChip
                        sd={this.props.sd}
                        setNextCallbackFunction={this.props.setNextCallbackFunction}
                        datasource={this.props.datasource}
                        currentActionId={this.props.actionToEdit?.id}
                        actionKind="select"
                        name={this.state.actionName}
                        labelTestId="selectActionNameLabel"
                        onManageAll={() => this.handleCloseDatasource()}
                      />
                    }
                    isRenaming={this.state.isNameOnEdit}
                    canAbortRename={!!this.state.actionName}
                    entityLabel="select action"
                    fieldName="ActionName"
                    rules={[{ validator: this.nameValidator }]}
                    placeholder="Select Action name"
                    testIds={SELECT_ACTION_RENAME_TEST_IDS}
                    onStartRename={this.handleStartRename}
                    onConfirmRename={(): void => void this.handleConfirmRename()}
                    onAbortRename={this.handleAbortRename}
                  />
                )}
                backLabel="Back to Datasources"
                submitLabel={this.props.actionToEdit ? "UPDATE" : "CREATE"}
                submitHtmlType="submit"
                status={this.state.saveStatus}
                isSubmitDisabled={!isGenericSelectAction && this.state.submitButtonDisabledStatus}
                onCancel={() => this.handleCloseDatasource()}
                cancelTestId="closeCreateActionPanel"
                submitTestId="actionCreateUpdateButton"
                cancelOnboardingId="data-diagram_create-action_close"
                submitOnboardingId="data-diagram_create-action_submit"
              />
              <div style={{ height: "100%" }}>
                <KuikaSpinnerSmall spinning={this.state.isCreating}>
                  <div className="kuika__sql-action-modal__generic-container" style={{ height: "100%", width: "100%" }}>
                    <GenericSqlPanelComponent
                      isModal={false}
                      datasourceId={this.props.datasource?.id ?? ""}
                      nameValidator={this.nameValidator}
                      genericSelectData={this.state.genericSelectData}
                      updateGenericSelectData={(data) => this.setState({ genericSelectData: data })}
                      convertToSql={this.convertGenericSelectToSql}
                    />
                  </div>
                </KuikaSpinnerSmall>
              </div>
            </>
          ) : (
            <div className="kuika__code-editor__page">
              <div className="kuika__code-editor__shell">
                <CodeEditorHeader
                  title={this.state.actionName || "SQL Action"}
                  renderTitle={(): React.ReactNode => (
                    <CodeEditorRenameTitle
                      identity={
                        <ActionSwitcherChip
                          sd={this.props.sd}
                          setNextCallbackFunction={this.props.setNextCallbackFunction}
                          datasource={this.props.datasource}
                          currentActionId={this.props.actionToEdit?.id}
                          actionKind="sql"
                          name={this.state.actionName}
                          // The old "SQL EDITOR" heading's job — the observable signal that the
                          // panel switched from the select editor into this one (see CONVERT).
                          labelTestId="sqlEditorTitle"
                          onManageAll={() => this.handleCloseDatasource()}
                        />
                      }
                      isRenaming={this.state.isNameOnEdit}
                      canAbortRename={!!this.state.actionName}
                      entityLabel="SQL action"
                      fieldName="ActionName"
                      rules={[{ validator: this.nameValidator }]}
                      placeholder="Custom action name"
                      testIds={codeEditorRenameTestIds("actionName")}
                      inputOnboardingId="data-diagram_create-action_name-input"
                      onStartRename={this.handleStartRename}
                      onConfirmRename={(): void => void this.handleConfirmRename()}
                      onAbortRename={this.handleAbortRename}
                    />
                  )}
                  backLabel="Back to Datasources"
                  runLabel="Run"
                  isRunning={this.state.isTestButtonLoading}
                  onRun={this.handleTest}
                  runTestId="actionTestButton"
                  saveLabel={this.props.actionToEdit ? "UPDATE" : "CREATE"}
                  saveStatus={this.state.saveStatus}
                  isSaveDisabled={this.state.submitButtonDisabledStatus}
                  saveTestId="actionCreateUpdateButton"
                  onClose={() => this.handleCloseDatasource()}
                  closeTestId="closeCreateActionPanel"
                />

                <div className="kuika__code-editor__body">
                  <div className="kuika__code-editor__main">
                    <CodeEditorToolbar
                      onUndo={this.handleUndo}
                      onRedo={this.handleRedo}
                      onCopy={this.handleCopyEditorContent}
                      onFormat={this.handleFormatSql}
                      formatCompleted={this.state.formatCompleted}
                      isWrapEnabled={this.state.wrapStatus === "on"}
                      onToggleWrap={() => this.handleWrap(this.state.wrapStatus === "on" ? "off" : "on")}
                      onPasteAtCursor={this.handlePasteToLastClickedRow}
                      fontSize={this.state.selectedPunto}
                      onFontSizeChange={this.handleEditorPuntoChange}
                      problems={this.state.problems}
                      runError={this.state.runError}
                      onShowProblems={(): void => this.setState({ drawerTab: "messages", isResultDrawerOpen: true })}
                      cursor={this.state.cursor}
                    />

                    {this.state.pendingAiEdit && (
                      <AiPendingEditBar
                        plan={this.state.pendingAiEdit}
                        subject="query"
                        onApply={this.handleApplyPendingAiEdit}
                        onDiscard={this.aiPendingEdit.discard}
                        onChangeMode={this.aiPendingEdit.setMode}
                      />
                    )}

                    <div className="kuika__code-editor__editor">
                      <Form.Item required name="Statement">
                        <Editor
                          wrapperProps={SQL_EDITOR_WRAPPER_PROPS}
                          data-onboarding-id="data-diagram_create-action_sql-editor"
                          language="sql"
                          theme={CODE_EDITOR_THEME}
                          options={{
                            selectOnLineNumbers: true,
                            automaticLayout: true,
                            fontSize: this.state.selectedPunto
                          }}
                          onChange={this.onChange}
                          onMount={this.handleEditorMount}
                          beforeMount={this.editorBeforeMount}
                        />
                      </Form.Item>
                    </div>

                    {this.state.isResultDrawerOpen && (
                      <CodeEditorSplitter
                        orientation="horizontal"
                        size={this.state.drawerHeight}
                        onSizeChange={(drawerHeight) => this.setState({ drawerHeight })}
                        minSize={120}
                        maxSize={640}
                      />
                    )}

                    <CodeEditorDrawer
                      height={this.state.drawerHeight}
                      isCollapsed={!this.state.isResultDrawerOpen}
                      onToggleCollapsed={(isCollapsed: boolean): void =>
                        this.setState({ isResultDrawerOpen: !isCollapsed })
                      }
                      activeTab={this.state.drawerTab}
                      onTabChange={(drawerTab) => this.setState({ drawerTab })}
                      resultCount={this.state.isResultListOpen ? this.state.resultList.length : null}
                      messageCount={this.state.problems.length + (this.state.runError ? 1 : 0)}
                      meta={this.getRunMeta()}
                      resultContent={this.renderResultTable()}
                      messagesContent={this.renderProblems()}
                    />
                  </div>

                  <CodeEditorSplitter
                    orientation="vertical"
                    size={this.state.sidePanelWidth}
                    onSizeChange={(sidePanelWidth) => this.setState({ sidePanelWidth })}
                    minSize={240}
                    maxSize={520}
                    isCollapsed={!this.state.isSidePanelOpened}
                    onToggleCollapsed={(isCollapsed) => this.setState({ isSidePanelOpened: !isCollapsed })}
                  />

                  {this.state.isSidePanelOpened && (
                    <CodeEditorSidePanel
                      width={this.state.sidePanelWidth}
                      activeTab={this.state.sideTab}
                      onTabChange={(sideTab) => this.setState({ sideTab })}
                      parameterCount={this.state.actionParameters.length}
                      coachContent={
                        coachType === null ? null : (
                          <AICodeGenerator
                            type={coachType}
                            tables={tables}
                            datasourceId={this.props.datasource?.id}
                            suggestions={SQL_ACTION_AI_SUGGESTIONS}
                            onApplyCode={this.aiPendingEdit.request}
                          />
                        )
                      }
                      // The parameters come back from the server: previewed while there are none to show,
                      // kept on screen while a later edit re-reads their types.
                      parametersContent={
                        resolveLoadingPresentation(
                          this.state.isParametersLoading,
                          this.state.actionParameters.length > 0
                        ) === "skeleton" ? (
                          <KuikaSkeletonRegion label="Loading parameters">
                            <KuikaSkeletonList rows={3} density="compact" />
                          </KuikaSkeletonRegion>
                        ) : (
                          <SQLActionParameters
                            actionParameters={this.state.actionParameters}
                            updateParameters={(actionParameters) => {
                              this.setState({ actionParameters });
                              this.forceUpdate();
                              this.setSubmitButtonDisabledStatus();
                            }}
                            searchable
                            showTypeIcons
                            searchableTypes
                            markUnknownTypes
                            checkedTypeNames={this.state.checkedTypeNames}
                            emptyStateName="parameter"
                            emptyStateIcon="adjustments-horizontal"
                            emptyStateDescription="Add a parameter to your query with @name to see it here."
                          />
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <CloseConfirmationModal
          itemName={this.state.actionName}
          closeType={"Action"}
          triggerModal={this.props.showModal}
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </Form>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createAction = connect(mapStateToProps, mapDispatchToProps)(withSD<ICreateActionProps>(CreateAction));
export { createAction as CreateAction };
